/* eslint-disable max-lines-per-function, complexity */
import { computed, ref } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import { injectService } from '@patsnap/tahiti-core';
import {
    UserSettingsApi,
    type UpdateUserSettingsRequest,
    type UserSettingsApi as UserSettingsApiType,
} from '../apis/user-settings-api';
import type {
    IpSearchFirstTaskRewardSettingsV1,
    IpSearchOnboardingSettingsV1,
    ModelStrategy,
    ModelStrategySettingKey,
    NoveltyCheckJudgmentPreference,
    NoveltyCheckShowPatentLimit,
    UserSettings,
} from '../types';
import { isUserLogin } from '@telecom/shared/startup/user-auth';

/**
 * 前端用户设置默认值
 */
export const DEFAULT_USER_SETTINGS = {
    novelty_check_auto_run: true,
    novelty_check_family_merge: true,
    novelty_check_show_patent_limit: 'all',
    novelty_check_judgment_preference: 'strict',
    novelty_check_model_strategy: 'AUTO',
    fto_model_strategy: 'AUTO',
    design_fto_model_strategy: 'AUTO',
} satisfies Required<Pick<
    UserSettings,
    | 'novelty_check_auto_run'
    | 'novelty_check_family_merge'
    | 'novelty_check_show_patent_limit'
    | 'novelty_check_judgment_preference'
    | 'novelty_check_model_strategy'
    | 'fto_model_strategy'
    | 'design_fto_model_strategy'
>>;

const MODEL_STRATEGY_SETTING_FIELDS = [
    'novelty_check_model_strategy',
    'fto_model_strategy',
    'design_fto_model_strategy',
] as const satisfies readonly ModelStrategySettingKey[];

const SUPPORTED_USER_SETTING_FIELDS = [
    'novelty_check_auto_run',
    'novelty_check_family_merge',
    'novelty_check_show_patent_limit',
    'novelty_check_judgment_preference',
    ...MODEL_STRATEGY_SETTING_FIELDS,
    'ip_search_onboarding_v1',
    'ip_search_first_task_reward_v1',
] as const;

type SupportedUserSettingField = typeof SUPPORTED_USER_SETTING_FIELDS[number];

function isSupportedUserSettingField(field: string): field is SupportedUserSettingField {
    return (SUPPORTED_USER_SETTING_FIELDS as readonly string[]).includes(field);
}

function isPatentLimitValue(value: unknown): value is NoveltyCheckShowPatentLimit {
    return value === 10 || value === 20 || value === 50 || value === 100 || value === 'all';
}

function isJudgmentPreferenceValue(value: unknown): value is NoveltyCheckJudgmentPreference {
    return value === 'strict' || value === 'medium' || value === 'loose';
}

function isModelStrategyValue(value: unknown): value is ModelStrategy {
    return value === 'AUTO' || value === 'ALIYUN' || value === 'DEEPSEEK' || value === 'DOUBAO';
}

function isModelStrategySettingField(field: string): field is ModelStrategySettingKey {
    return (MODEL_STRATEGY_SETTING_FIELDS as readonly string[]).includes(field);
}

/**
 * 过滤后端或调用方传入的 IP Search onboarding 状态。
 * @param value 待校验的原始状态
 */
export function sanitizeIpSearchOnboardingSettings(
    value: unknown,
): IpSearchOnboardingSettingsV1 | null | undefined {
    if (value === null) return null;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;

    const source = value as Record<string, unknown>;
    const result: IpSearchOnboardingSettingsV1 = {};
    if (
        source.entry_status === 'eligible'
        || source.entry_status === 'ineligible'
        || source.entry_status === 'seen'
    ) {
        result.entry_status = source.entry_status;
    }
    if (
        source.report_eligibility_status === 'eligible'
        || source.report_eligibility_status === 'ineligible'
    ) {
        result.report_eligibility_status = source.report_eligibility_status;
    }
    if (typeof source.novelty_report_actions_seen === 'boolean') {
        result.novelty_report_actions_seen = source.novelty_report_actions_seen;
    }
    if (typeof source.fto_report_actions_seen === 'boolean') {
        result.fto_report_actions_seen = source.fto_report_actions_seen;
    }
    if (typeof source.design_fto_report_actions_seen === 'boolean') {
        result.design_fto_report_actions_seen = source.design_fto_report_actions_seen;
    }
    if (typeof source.first_task_reward_prompt_seen === 'boolean') {
        result.first_task_reward_prompt_seen = source.first_task_reward_prompt_seen;
    }
    return result;
}

/** 过滤后端返回的首次任务奖励状态。 */
function sanitizeFirstTaskRewardSettings(value: unknown): IpSearchFirstTaskRewardSettingsV1 | null | undefined {
    if (value === null) return null;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;

    const source = value as Record<string, unknown>;
    const result: IpSearchFirstTaskRewardSettingsV1 = {};
    if (source.status === 'PROCESSING' || source.status === 'GRANTED' || source.status === 'FAILED') {
        result.status = source.status;
    }
    if (typeof source.task_id === 'string') result.task_id = source.task_id;
    if (typeof source.agent_type === 'string') result.agent_type = source.agent_type;
    if (typeof source.action === 'string') result.action = source.action;
    if (typeof source.granted_at === 'number') result.granted_at = source.granted_at;
    if (typeof source.expires_at === 'number') result.expires_at = source.expires_at;
    return result;
}

function sanitizeUserSettings(settings: UserSettings): UserSettings {
    return Object.entries(settings).reduce<UserSettings>((result, [key, value]) => {
        if (!isSupportedUserSettingField(key)) {
            return result;
        }

        if (key === 'novelty_check_auto_run' || key === 'novelty_check_family_merge') {
            if (typeof value === 'boolean' || value === null) {
                result[key] = value;
            }
        } else if (key === 'novelty_check_show_patent_limit' && (isPatentLimitValue(value) || value === null)) {
            result.novelty_check_show_patent_limit = value;
        } else if (key === 'novelty_check_judgment_preference' && (isJudgmentPreferenceValue(value) || value === null)) {
            result.novelty_check_judgment_preference = value;
        } else if (isModelStrategySettingField(key) && (isModelStrategyValue(value) || value === null)) {
            result[key] = value;
        } else if (key === 'ip_search_onboarding_v1') {
            const onboardingSettings = sanitizeIpSearchOnboardingSettings(value);
            if (onboardingSettings !== undefined) result.ip_search_onboarding_v1 = onboardingSettings;
        } else if (key === 'ip_search_first_task_reward_v1') {
            const rewardSettings = sanitizeFirstTaskRewardSettings(value);
            if (rewardSettings !== undefined) result.ip_search_first_task_reward_v1 = rewardSettings;
        }
        return result;
    }, {});
}

/**
 * 合并用户设置与默认值
 * @param settings 后端返回或本地保存的设置
 */
export function getEffectiveUserSettings(settings: UserSettings) {
    const sanitizedSettings = sanitizeUserSettings(settings);
    return {
        novelty_check_auto_run: typeof sanitizedSettings.novelty_check_auto_run === 'boolean'
            ? sanitizedSettings.novelty_check_auto_run
            : DEFAULT_USER_SETTINGS.novelty_check_auto_run,
        novelty_check_family_merge: typeof sanitizedSettings.novelty_check_family_merge === 'boolean'
            ? sanitizedSettings.novelty_check_family_merge
            : DEFAULT_USER_SETTINGS.novelty_check_family_merge,
        novelty_check_show_patent_limit: isPatentLimitValue(sanitizedSettings.novelty_check_show_patent_limit)
            ? sanitizedSettings.novelty_check_show_patent_limit
            : DEFAULT_USER_SETTINGS.novelty_check_show_patent_limit,
        novelty_check_judgment_preference: isJudgmentPreferenceValue(sanitizedSettings.novelty_check_judgment_preference)
            ? sanitizedSettings.novelty_check_judgment_preference
            : DEFAULT_USER_SETTINGS.novelty_check_judgment_preference,
        novelty_check_model_strategy: isModelStrategyValue(sanitizedSettings.novelty_check_model_strategy)
            ? sanitizedSettings.novelty_check_model_strategy
            : DEFAULT_USER_SETTINGS.novelty_check_model_strategy,
        fto_model_strategy: isModelStrategyValue(sanitizedSettings.fto_model_strategy)
            ? sanitizedSettings.fto_model_strategy
            : DEFAULT_USER_SETTINGS.fto_model_strategy,
        design_fto_model_strategy: isModelStrategyValue(sanitizedSettings.design_fto_model_strategy)
            ? sanitizedSettings.design_fto_model_strategy
            : DEFAULT_USER_SETTINGS.design_fto_model_strategy,
        ...('ip_search_onboarding_v1' in sanitizedSettings
            ? { ip_search_onboarding_v1: sanitizedSettings.ip_search_onboarding_v1 }
            : {}),
        ...('ip_search_first_task_reward_v1' in sanitizedSettings
            ? { ip_search_first_task_reward_v1: sanitizedSettings.ip_search_first_task_reward_v1 }
            : {}),
    };
}

/**
 * 构建更新用户设置请求
 * @param settings 待更新设置
 */
export function createUpdateUserSettingsRequest(settings: UserSettings): UpdateUserSettingsRequest {
    const clientWritableSettings = { ...settings };
    delete clientWritableSettings.ip_search_first_task_reward_v1;
    return {
        settings: sanitizeUserSettings(clientWritableSettings),
    };
}

/**
 * 全局用户设置状态
 */
export const useUserSettingsState = createSharedComposable(() => {
    const userSettingsApi = injectService<UserSettingsApiType>(UserSettingsApi);
    const settings = ref<UserSettings>({});
    const updatedAt = ref<number | null>(null);
    const loading = ref(false);
    const saving = ref(false);
    const loaded = ref(false);
    let loadingPromise: Promise<void> | null = null;

    const effectiveSettings = computed(() => getEffectiveUserSettings(settings.value));

    async function loadSettings(force = false): Promise<void> {
        if (!(await isUserLogin())) return;
        if (loaded.value && !force) return;
        if (loadingPromise && !force) return loadingPromise;

        loading.value = true;
        loadingPromise = userSettingsApi.getUserSettings()
            .then((response) => {
                settings.value = sanitizeUserSettings(response.settings ?? {});
                updatedAt.value = response.updated_at;
                loaded.value = true;
            })
            .finally(() => {
                loading.value = false;
                loadingPromise = null;
            });

        return loadingPromise;
    }

    async function updateSettings(nextSettings: UserSettings): Promise<void> {
        if (!(await isUserLogin())) return;
        const previousSettings = settings.value;
        const previousUpdatedAt = updatedAt.value;
        saving.value = true;
        try {
            const response = await userSettingsApi.updateUserSettings(
                createUpdateUserSettingsRequest(nextSettings),
            );
            settings.value = sanitizeUserSettings(response.settings ?? {});
            updatedAt.value = response.updated_at;
            loaded.value = true;
        } catch (error) {
            settings.value = previousSettings;
            updatedAt.value = previousUpdatedAt;
            throw error;
        } finally {
            saving.value = false;
        }
    }

    return {
        settings,
        effectiveSettings,
        updatedAt,
        loading,
        saving,
        loaded,
        loadSettings,
        updateSettings,
    };
});
